import { env } from '@/config/environment';

export interface TrailRecentUser {
  userId: string;
  avatarUrl: string | null;
}

const CACHE_TTL_MS = 2 * 60 * 1000;

interface CacheEntry {
  users: TrailRecentUser[];
  expiresAt: number;
}

class TrailRecentUsersService {
  private baseUrl: string;
  private cache = new Map<string, CacheEntry>();

  constructor() {
    this.baseUrl = env.apiUrl;
  }

  getCachedUsers(trailSlug: string): TrailRecentUser[] | null {
    const cached = this.cache.get(trailSlug);
    if (cached && Date.now() < cached.expiresAt) {
      return cached.users;
    }
    return null;
  }

  async getRecentUsers(trailSlug: string): Promise<TrailRecentUser[]> {
    const cached = this.cache.get(trailSlug);
    if (cached && Date.now() < cached.expiresAt) {
      return cached.users;
    }

    try {
      const response = await fetch(
        `${this.baseUrl}/api/trails/${encodeURIComponent(trailSlug)}/recent-users`
      );
      const data = await response.json();

      if (data.success) {
        this.cache.set(trailSlug, {
          users: data.recentUsers,
          expiresAt: Date.now() + CACHE_TTL_MS,
        });
        return data.recentUsers;
      }

      console.error('Failed to fetch trail recent users:', data.error);
      return [];
    } catch (error) {
      console.error('Error fetching trail recent users:', error);
      return [];
    }
  }

  invalidate(trailSlug?: string) {
    if (trailSlug) {
      this.cache.delete(trailSlug);
    } else {
      this.cache.clear();
    }
  }
}

export const trailRecentUsersService = new TrailRecentUsersService();
export default trailRecentUsersService;
